import { useEffect, useState } from "react";
import { GearSix, Key, Clock, CheckCircle, ShieldCheck } from "@phosphor-icons/react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function Settings() {
  const [s, setS] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [airlines, setAirlines] = useState([]);

  const load = async () => {
    const r = await api.get("/settings");
    setS(r.data);
    const r2 = await api.get("/routes");
    setRoutes(r2.data);
    const r3 = await api.get("/settings/airlines");
    setAirlines(r3.data);
  };

  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    const next = { ...s, ...patch };
    setS(next);
    await api.put("/settings", patch);
    toast.success("Settings updated");
  };

  const toggleRoute = async (rid, active) => {
    await api.put(`/settings/routes/${rid}`, { is_active: active });
    toast.success("Route updated");
    load();
  };

  const toggleCompetitor = async (route, iata) => {
    const comps = route.competitors.includes(iata)
      ? route.competitors.filter((c) => c !== iata)
      : [...route.competitors, iata];
    await api.put(`/settings/routes/${route.id}`, { competitors: comps });
    load();
  };

  if (!s) return <div className="p-10 text-sm" style={{ color: "var(--text-secondary)" }}>Loading...</div>;

  return (
    <div className="p-6 lg:p-10 max-w-[1400px]">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em]" style={{ color: "var(--saa-gold)" }}>
          <GearSix size={14} weight="duotone" /> Admin
        </div>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-2" data-testid="settings-title">Settings</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Automatic Refresh — full panel */}
        <div className="saa-card p-5 lg:col-span-2" data-testid="auto-refresh-panel">
          <div className="flex items-center gap-2 mb-3">
            <Clock size={16} weight="duotone" color="var(--saa-gold)" />
            <h2 className="font-display text-lg font-semibold">Automatic refresh schedule</h2>
          </div>

          <div className="flex items-center justify-between p-3 rounded-lg mb-4"
               style={{ background: "var(--card-bg-2)" }}>
            <div>
              <div className="text-sm font-medium">Auto-refresh enabled</div>
              <div className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>
                {s.auto_schedule
                  ? "Pipeline runs automatically on the days + hour selected below."
                  : "Auto-refresh is OFF. Data only updates when you click Refresh on Dashboard / Data Sources."}
              </div>
            </div>
            <Switch
              data-testid="auto-schedule-switch"
              checked={s.auto_schedule}
              onCheckedChange={(v) => save({ auto_schedule: v })}
            />
          </div>

          {/* Day-of-week selector */}
          <div className="mb-4">
            <div className="text-xs uppercase tracking-[0.16em] mb-2"
                 style={{ color: "var(--text-secondary)" }}>
              Refresh on these days (UTC)
            </div>
            <div className="flex flex-wrap gap-2">
              {DOW.map((d, i) => {
                const selected = (s.schedule_days || []).includes(i);
                return (
                  <button
                    key={i}
                    data-testid={`schedule-day-${i}`}
                    onClick={() => {
                      const cur = new Set(s.schedule_days || []);
                      if (cur.has(i)) cur.delete(i); else cur.add(i);
                      const next = Array.from(cur).sort();
                      save({ schedule_days: next.length ? next : [0, 2, 4] });
                    }}
                    disabled={!s.auto_schedule}
                    className="font-mono text-xs px-3 py-1.5 rounded-full transition-all"
                    style={{
                      background: selected ? "var(--saa-gold)" : "var(--card-bg-2)",
                      color: selected ? "#0A1428" : "var(--text-secondary)",
                      border: selected ? "1px solid var(--saa-gold)" : "1px solid var(--border-subtle)",
                      opacity: s.auto_schedule ? 1 : 0.4,
                      cursor: s.auto_schedule ? "pointer" : "not-allowed",
                      fontWeight: selected ? 600 : 400,
                    }}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Hour selector */}
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium">Refresh hour (UTC)</div>
              <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
                Recommended: 02:00 UTC (low fare-API traffic)
              </div>
            </div>
            <Select
              value={`${s.schedule_hour_utc ?? 2}`}
              onValueChange={(v) => save({ schedule_hour_utc: parseInt(v) })}
              disabled={!s.auto_schedule}
            >
              <SelectTrigger className="w-[140px]" data-testid="schedule-hour-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 24 }).map((_, h) => (
                  <SelectItem key={h} value={`${h}`}>{h.toString().padStart(2, "0")}:00 UTC</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Persistence reassurance */}
          <div className="mt-4 p-3 rounded-lg flex gap-2 items-start"
               style={{ background: "rgba(16, 185, 129, 0.08)",
                        border: "1px solid rgba(16, 185, 129, 0.22)" }}>
            <CheckCircle size={16} weight="duotone" color="#10B981" className="mt-0.5 shrink-0" />
            <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
              <span className="font-semibold" style={{ color: "var(--text-primary)" }}>
                All fetched fares are persisted server-side.
              </span>{" "}
              Every refresh (automatic or manual) writes to a shared MongoDB instance.
              The data remains visible to all users even if your device is offline or shut down —
              the scheduler keeps running on the server.
            </div>
          </div>
        </div>

        {/* Data Integrity */}
        <div className="saa-card p-5 lg:col-span-2" data-testid="integrity-panel">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck size={16} weight="duotone" color="var(--success)" />
            <h2 className="font-display text-lg font-semibold">Data integrity</h2>
          </div>
          <div className="flex items-start justify-between gap-4 p-3 rounded-lg"
               style={{ background: "var(--card-bg-2)" }}>
            <div className="flex-1">
              <div className="text-sm font-medium">Strict mode (no fabricated data)</div>
              <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
                {s.data_integrity_mode
                  ? "ON — pipeline refuses to invent fares. Cells without a verified source (Duffel NDC, Google Flights, Trip.com, Airline Direct, or admin override) are left empty. Recommended for production use."
                  : "OFF — pipeline fills missing cells with formula-generated 'estimated' fares for demo purposes. NOT recommended for commercial decisions."}
              </div>
            </div>
            <Switch
              data-testid="integrity-switch"
              checked={!!s.data_integrity_mode}
              onCheckedChange={(v) => save({ data_integrity_mode: v })}
            />
          </div>
        </div>

        {/* API keys */}
        <div className="saa-card p-5">
          <div className="flex items-center gap-2 mb-3">
            <Key size={16} weight="duotone" color="var(--saa-gold)" />
            <h2 className="font-display text-lg font-semibold">API Keys</h2>
          </div>
          <div className="space-y-3 text-sm">
            <KV label="SerpAPI" value={s.api_keys?.serpapi} />
            <KV label="Zyte" value={s.api_keys?.zyte} />
            <KV label="Duffel NDC" value={s.api_keys?.duffel} />
            <p className="text-xs mt-2" style={{ color: "var(--text-secondary)" }}>
              Keys are read from environment variables. Edit /app/backend/.env and add DUFFEL_API_TOKEN to enable NDC.
            </p>
          </div>
        </div>

        {/* Schedule + FX (existing) */}
        <div className="saa-card p-5">
          <div className="flex items-center gap-2 mb-3">
            <Clock size={16} weight="duotone" color="var(--saa-gold)" />
            <h2 className="font-display text-lg font-semibold">Sampling & FX</h2>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-medium">Sample day-of-week</div>
                <div className="text-xs" style={{ color: "var(--text-secondary)" }}>Outbound date</div>
              </div>
              <Select value={`${s.sample_day_of_week}`} onValueChange={(v) => save({ sample_day_of_week: parseInt(v) })}>
                <SelectTrigger className="w-[120px]" data-testid="dow-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DOW.map((d, i) => <SelectItem key={i} value={`${i}`}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-medium">Return window</div>
                <div className="text-xs" style={{ color: "var(--text-secondary)" }}>Nights from outbound</div>
              </div>
              <Select value={`${s.return_window_nights}`} onValueChange={(v) => save({ return_window_nights: parseInt(v) })}>
                <SelectTrigger className="w-[120px]" data-testid="return-window-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 nights</SelectItem>
                  <SelectItem value="10">10 nights</SelectItem>
                  <SelectItem value="14">14 nights</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-medium">USD → AUD FX</div>
                <div className="text-xs" style={{ color: "var(--text-secondary)" }}>Hot-reloadable</div>
              </div>
              <Input
                type="number"
                step="0.01"
                className="w-[120px]"
                data-testid="fx-input"
                value={s.usd_to_aud_fx}
                onChange={(e) => setS({ ...s, usd_to_aud_fx: parseFloat(e.target.value) })}
                onBlur={() => save({ usd_to_aud_fx: s.usd_to_aud_fx })}
              />
            </div>
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-medium">Alert threshold</div>
                <div className="text-xs" style={{ color: "var(--text-secondary)" }}>% cheaper to trigger</div>
              </div>
              <Input
                type="number"
                step="1"
                className="w-[120px]"
                data-testid="threshold-input"
                value={s.alert_threshold_pct}
                onChange={(e) => setS({ ...s, alert_threshold_pct: parseFloat(e.target.value) })}
                onBlur={() => save({ alert_threshold_pct: s.alert_threshold_pct })}
              />
            </div>
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-medium">Samples per month</div>
                <div className="text-xs" style={{ color: "var(--text-secondary)" }}>4 = weekly (default); higher = tighter monthly avg</div>
              </div>
              <Input
                type="number"
                min="2" max="12" step="1"
                className="w-[120px]"
                data-testid="samples-input"
                value={s.samples_per_month ?? 4}
                onChange={(e) => setS({ ...s, samples_per_month: parseInt(e.target.value) })}
                onBlur={() => save({ samples_per_month: s.samples_per_month })}
              />
            </div>
          </div>
        </div>

        {/* Data Tier toggles */}
        <div className="saa-card p-5 lg:col-span-2">
          <div className="flex items-center gap-2 mb-3">
            <Key size={16} weight="duotone" color="var(--saa-gold)" />
            <h2 className="font-display text-lg font-semibold">Active data tiers</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ToggleRow
              label="Duffel NDC"
              hint="Authoritative airline-direct via aggregator. Add DUFFEL_API_TOKEN to enable."
              checked={!!s.enable_duffel}
              onChange={(v) => save({ enable_duffel: v })}
              testid="enable-duffel"
              statusBadge={s.api_keys?.duffel ? "configured" : "no_key"}
            />
            <ToggleRow
              label="SerpAPI Google Flights"
              hint="Cheapest fares from each airline via Google distribution."
              checked={!!s.enable_serpapi}
              onChange={(v) => save({ enable_serpapi: v })}
              testid="enable-serpapi"
              statusBadge={s.api_keys?.serpapi ? "configured" : "no_key"}
            />
            <ToggleRow
              label="SerpAPI Trip.com"
              hint="Independent second source — powers disagreement detector."
              checked={!!s.enable_tripcom}
              onChange={(v) => save({ enable_tripcom: v })}
              testid="enable-tripcom"
              statusBadge={s.api_keys?.serpapi ? "configured" : "no_key"}
            />
          </div>
        </div>
      </div>

      {/* Routes management */}
      <div className="saa-card p-5 mt-6">
        <h2 className="font-display text-lg font-semibold mb-3">Routes ({routes.length})</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ color: "var(--text-secondary)" }} className="text-[11px] uppercase tracking-[0.12em]">
                <th className="text-left pb-2">Route</th>
                <th className="text-left pb-2">Active</th>
                <th className="text-left pb-2">Competitors</th>
              </tr>
            </thead>
            <tbody>
              {routes.map((r) => (
                <tr key={r.id} style={{ borderTop: "1px solid var(--border-subtle)" }}>
                  <td className="py-2.5 font-mono font-semibold">{r.id}</td>
                  <td className="py-2.5">
                    <Switch
                      data-testid={`route-active-${r.id}`}
                      checked={r.is_active}
                      onCheckedChange={(v) => toggleRoute(r.id, v)}
                    />
                  </td>
                  <td className="py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {airlines.filter((a) => a.iata !== "SA").map((a) => {
                        const on = r.competitors.includes(a.iata);
                        return (
                          <button
                            key={a.iata}
                            onClick={() => toggleCompetitor(r, a.iata)}
                            data-testid={`comp-${r.id}-${a.iata}`}
                            className="font-mono text-[11px] px-2 py-0.5 rounded transition-colors"
                            style={{
                              background: on ? a.color : "var(--card-bg-2)",
                              color: on ? "#fff" : "var(--text-muted)",
                              opacity: on ? 1 : 0.6,
                            }}
                          >
                            {a.iata}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span style={{ color: "var(--text-secondary)" }}>{label}</span>
      <span className="font-mono text-xs" style={{ color: "var(--text-primary)" }}>{value || "—"}</span>
    </div>
  );
}

function ToggleRow({ label, hint, checked, onChange, testid, statusBadge }) {
  return (
    <div className="p-3 rounded-lg" style={{ background: "var(--card-bg-2)", border: "1px solid var(--border-subtle)" }}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-sm font-medium">{label}</div>
        <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
      </div>
      <div className="text-[11px]" style={{ color: "var(--text-secondary)" }}>{hint}</div>
      {statusBadge === "no_key" && (
        <div className="mt-2 text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--warning)" }}>
          API key not configured
        </div>
      )}
      {statusBadge === "configured" && (
        <div className="mt-2 text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--success)" }}>
          ✓ Key present
        </div>
      )}
    </div>
  );
}
